import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { X } from 'react-feather';
import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import { CalendarAlert } from '@/utils/CalendarAlert/CalendarAlert';
import { FranchiseeBundleAddOnRow } from '@/types/namedPrice.types';
import styles from './EditBundleAddOnDrawer.module.css';
import StoreGroupPricesTable, { StoreGroupWithCount } from './StoreGroupPricesTable';
import BundleAddOnConfirmation from './BundleAddOnConfirmation';
import { NamedPricePriceDeviationsDrawer } from '@/pages/Franchisee/Bundles/BundleAddOns/NamedPricePriceDeviationsDrawer/NamedPricePriceDeviationsDrawer';
import { useNamedPriceStoreOverride } from '@/services/namedPrice/namedPrice.service';
import { useExistsActiveGlobalFreezes } from '@/services/freeze/freeze.service';
import {
  EditDrawerSuccessAndFailure,
  EntityToUpdate,
} from '@/pages/Franchisee/SharedComponents/EditDrawerSuccessAndFailure';
import { EditDrawerProcessing } from '@/pages/Franchisee/SharedComponents/EditDrawerProcessing';
import { PriceUpdateAPIResponse } from '@/types/stores.types';
import { UserRoles } from '@/types/auth.types';
import useLocalUser from '@/utils/useLocalUser/useLocalUser';

export interface EditBundleAddOnDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  addOn: FranchiseeBundleAddOnRow | null;
  storeGroups: StoreGroupWithCount[];
  /** Selected store numbers (storeNo) for the store-override API */
  stores?: string[];
  onContinue?: (newPricePoint: string) => void;
  /** Called after save (success or error) to refresh data */
  afterSave?: () => void;
}

const EditBundleAddOnDrawer: FC<EditBundleAddOnDrawerProps> = ({
  isOpen,
  onClose,
  addOn,
  storeGroups,
  stores = [],
  onContinue,
  afterSave,
}) => {
  const { t } = useTranslation();
  const [newPricePoint, setNewPricePoint] = useState('');
  const [priceError, setPriceError] = useState('');
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [priceIsSaved, setPriceIsSaved] = useState(false);
  const [errorResponse, setErrorResponse] = useState<PriceUpdateAPIResponse | undefined>(undefined);
  const [successResponse, setSuccessResponse] = useState<PriceUpdateAPIResponse | undefined>(undefined);
  const [priceDeviationsDrawer, setPriceDeviationsDrawer] = useState<{
    isOpen: boolean;
    namedPriceId: string;
    selectedGroupName: string;
  }>({ isOpen: false, namedPriceId: '', selectedGroupName: '' });

  const {
    mutateAsync: storeOverrideMutation,
    data: updateResponse,
    isLoading: isSavingPrice,
  } = useNamedPriceStoreOverride();
  const { data: existsActiveGlobalFreeze } = useExistsActiveGlobalFreezes();
  const { user } = useLocalUser();
  const isViewOnly = user?.role === UserRoles.VIEW;
  const isPriceEditDisabled = Boolean(existsActiveGlobalFreeze || addOn?.isLock || isViewOnly);

  useEffect(() => {
    if (isOpen) {
      setNewPricePoint('');
      setPriceError('');
      setIsConfirmationOpen(false);
      setPriceIsSaved(false);
      setErrorResponse(undefined);
      setSuccessResponse(undefined);
    }
  }, [isOpen]);

  const handlePriceSelect = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setPriceError('');
    setNewPricePoint(e.target.value);
  }, []);

  const handleContinue = useCallback(() => {
    const val = parseFloat(newPricePoint);
    if (newPricePoint.trim() === '' || Number.isNaN(val) || val < 0) {
      setPriceError('Enter a valid price');
      return;
    }
    setIsConfirmationOpen(true);
  }, [newPricePoint]);

  const handleConfirmSubmit = useCallback(() => {
    if (!addOn || stores.length === 0) return;
    setIsConfirmationOpen(false);
    const price = parseFloat(newPricePoint).toFixed(2);
    storeOverrideMutation(
      {
        stores,
        namedPriceIds: [addOn.namedPriceId],
        price,
      },
      {
        onSuccess: (data) => {
          setPriceIsSaved(true);
          setErrorResponse(undefined);
          setSuccessResponse(data);
          afterSave?.();
        },
        onError: (error: any) => {
          if (
            error?.response?.status === 504 ||
            error?.code === 'ECONNABORTED' ||
            error?.message?.includes('timeout') ||
            error?.message?.includes('Gateway Timeout')
          ) {
            setErrorResponse({
              successStoresCount: 0,
              failedStores: [],
              status: 'TIMEOUT',
              message: t('requestProcessingModal.timeout.message'),
            });
          } else {
            setErrorResponse({
              successStoresCount: 0,
              failedStores: [],
              status: 'ERROR',
              message: error?.message || t('editBundleAddOn.saveError'),
            });
          }
          setPriceIsSaved(true);
          afterSave?.();
        },
      },
    );
  }, [addOn, newPricePoint, stores, storeOverrideMutation, afterSave, t]);

  const priceOptions = useMemo(() => {
    if (!addOn) return [];
    if (addOn.availablePrices?.length) {
      return [...addOn.availablePrices]
        .sort((a, b) => a - b)
        .map((p) => ({
          value: (Math.round(p * 100) / 100).toFixed(2),
          label: `$${(Math.round(p * 100) / 100).toFixed(2)}`,
        }));
    }
    if (!addOn.storeGroupPrices) return [];
    const prices = new Set<number>();
    Object.values(addOn.storeGroupPrices).forEach((sg) => {
      const display = sg.priceDisplay?.replace(/^\$/, '').trim() ?? '';
      const single = display.includes(' - ') ? display.split(' - ')[0]?.trim() : display;
      const num = parseFloat(single?.replace(/[^0-9.]/g, '') ?? '');
      if (!Number.isNaN(num)) prices.add(Math.round(num * 100) / 100);
    });
    return [...prices].sort((a, b) => a - b).map((p) => ({ value: p.toFixed(2), label: `$${p.toFixed(2)}` }));
  }, [addOn]);

  const continueDisabled =
    isPriceEditDisabled || newPricePoint.trim() === '' || (parseFloat(newPricePoint) < 0 && newPricePoint !== '');

  if (!addOn) return null;

  return (
    <>
      <SwipeableDrawer
        open={isOpen}
        onClose={onClose}
        onOpen={() => {
          /* Required by SwipeableDrawer */
        }}
        anchor="bottom"
      >
        <div className={styles.container} data-testid="edit-bundle-add-on-drawer">
          <header>
            <div>
              <h1>{t('editBundleAddOn.title')}</h1>
            </div>
            <button type="button" onClick={onClose} aria-label="Close">
              <X data-testid="edit-bundle-add-on-drawer-close" size={24} color="var(--black-900)" />
            </button>
          </header>
          <div className={styles.scrollBody}>
            <div className={styles.alertBanner}>
              <CalendarAlert requestedVerbiage />
            </div>
            <div className={styles.content}>
              {isSavingPrice ? (
                <EditDrawerProcessing count={stores.length} entityToUpdate={EntityToUpdate.BUNDLE_UC} />
              ) : priceIsSaved ? (
                <EditDrawerSuccessAndFailure
                  apiResponse={
                    errorResponse ??
                    successResponse ??
                    updateResponse ?? {
                      successStoresCount: 0,
                      failedStores: [],
                      status: 'ERROR',
                      message: t('editBundleAddOn.saveError'),
                    }
                  }
                  entityToUpdate={EntityToUpdate.BUNDLE_UC}
                />
              ) : (
                <>
                  <div className={styles.productSettings}>
                    <strong>{t('editBundleAddOn.editBundleAddOnPriceSection')}</strong>
                    <label className={styles.label} htmlFor="edit-bundle-add-on-price-select">
                      {t('editBundleAddOn.newPricePointLabel')}
                    </label>
                    <div className={styles.priceInputWrap}>
                      {!newPricePoint.trim() && <span className={styles.currencyPrefix}>$</span>}
                      <select
                        id="edit-bundle-add-on-price-select"
                        className={styles.priceSelect}
                        aria-label={t('editBundleAddOn.newPricePointLabel')}
                        value={newPricePoint}
                        onChange={handlePriceSelect}
                        disabled={isPriceEditDisabled}
                        data-testid="edit-bundle-add-on-new-price"
                      >
                        <option value=""> </option>
                        {priceOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {priceError && <span className={styles.errorText}>{priceError}</span>}
                    <div className={styles.buttonContainer}>
                      <Button
                        variant={ButtonVariants.Primary}
                        onClick={handleContinue}
                        className={styles.submitButton}
                        disabled={continueDisabled}
                        data-testid="edit-bundle-add-on-continue"
                      >
                        {t('editBundleAddOn.continueButton')}
                      </Button>
                    </div>
                  </div>
                  <div className={styles.tableContainer}>
                    <StoreGroupPricesTable
                      addOn={addOn}
                      storeGroups={storeGroups}
                      onPriceDeviationsClick={(namedPriceId, selectedGroupName) => {
                        setPriceDeviationsDrawer({ isOpen: true, namedPriceId, selectedGroupName });
                      }}
                    />
                    <div className={styles.affectedSection}>
                      <h3 className={styles.affectedTitle}>{t('editBundleAddOn.affectedBundlesTitle')}</h3>
                      <div className={styles.affectedContent} data-testid="edit-bundle-add-on-affected-bundles">
                        {addOn.effectedBundles?.length ? (
                          <ul className={styles.affectedList}>
                            <li>{addOn.effectedBundles.join(', ')}</li>
                          </ul>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </SwipeableDrawer>
      <BundleAddOnConfirmation
        isOpen={isConfirmationOpen}
        onClose={() => setIsConfirmationOpen(false)}
        onCancel={onClose}
        addOn={addOn}
        storeGroups={storeGroups}
        newPrice={newPricePoint}
        onSubmit={handleConfirmSubmit}
      />
      {priceDeviationsDrawer.isOpen && (
        <NamedPricePriceDeviationsDrawer
          isOpen={priceDeviationsDrawer.isOpen}
          onClose={() => setPriceDeviationsDrawer((prev) => ({ ...prev, isOpen: false }))}
          namedPriceId={priceDeviationsDrawer.namedPriceId}
          selectedGroupName={priceDeviationsDrawer.selectedGroupName}
          cameFromEditDrawer
        />
      )}
    </>
  );
};

export default EditBundleAddOnDrawer;
